import { Tab } from "@headlessui/react";
import { useEffect, useState } from "react";
import Container from "www/shared/components/container/Container";
import { classNames } from "www/shared/utils";
import Image, { StaticImageData } from "next/legacy/image";

const FeaturesSection = ({
  linkId,
  features,
  footerText,
  headerText,
  subtitleText,
}: {
  headerText: string;
  subtitleText: string;
  linkId: string;
  features: {
    title: string;
    description: string;
    image: StaticImageData;
  }[];
  footerText?: string;
}) => {
  const [activeTabIndex, setActiveTabIndex] = useState(0);
  const [isTimerActive, setIsTimerActive] = useState(true);

  useEffect(() => {
    let timerId: NodeJS.Timeout | undefined;

    // if (isTimerActive) {
    //   timerId = setTimeout(() => {
    //     setActiveTabIndex((prev) => (prev + 1) % features.length);
    //   }, 4000);
    // }

    return () => clearTimeout(timerId);
  }, [activeTabIndex, isTimerActive]);

  return (
    <section
      id={linkId}
      className="bg-green-700 relative py-16 lg:py-28 overflow-hidden bg-[url('/marketing/sponsors/features/features_bg.jpg')] bg-[center_top_-40px] lg:bg-[left_-280px_top_-110px]"
    >
      <Container>
        <div className="max-w-4xl flex flex-col items-center text-center text-white mx-auto">
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold">
            {headerText}
          </h2>
          <p className="mt-5 lg:mt-8 text-lg tracking-tight">{subtitleText}</p>
        </div>
        <Tab.Group
          className="mt-8 sm:mt-10 lg:mt-16 lg:grid items-start lg:items-center gap-y-2 sm:gap-y-6 lg:grid-cols-12 lg:pt-0 relative"
          as="div"
        >
          {
            <>
              <div className="-mx-4 flex overflow-x-auto pb-4 sm:mx-0 sm:overflow-visible sm:pb-0 lg:col-span-5">
                <Tab.List className="relative z-10 grid grid-rows-2 grid-cols-2 lg:grid-cols-1 lg:grid-rows-4 max-w-3xl lg:max-w-full gap-2 sm:gap-4 px-4 mx-auto sm:px-0 lg:mx-0 lg:block lg:gap-x-0 lg:gap-y-1 lg:whitespace-normal">
                  {features.map((feature, ii) => (
                    <div
                      className={classNames(
                        "flex items-center justify-center lg:block group relative text-center lg:text-left rounded-xl p-2 lg:rounded-l-xl lg:rounded-r-none lg:p-6 transition-colors",
                        activeTabIndex === ii
                          ? "bg-white/10 ring-1 ring-inset ring-white/10"
                          : "hover:bg-white/10 lg:hover:bg-white/5"
                      )}
                      onClick={() => setActiveTabIndex(ii)}
                      key={feature.title}
                    >
                      <h3>
                        <Tab className="text-white text-center lg:text-left text-lg font-semibold outline-none">
                          <span className="absolute inset-0 rounded-full lg:rounded-l-xl lg:rounded-r-none" />
                          {feature.title}
                        </Tab>
                      </h3>
                      <p className="mt-2 text-gray-50 text-sm text-left hidden lg:block">
                        {feature.description}
                      </p>
                    </div>
                  ))}
                </Tab.List>
              </div>
              {features.map(
                (feature, ii) =>
                  activeTabIndex === ii && (
                    <div key={feature.title}>
                      <div className="relative lg:hidden">
                        <p className="relative mx-auto sm:mt-3 lg:mt-0 text-left md:max-w-[740px] lg:max-w-full text-base text-white lg:text-center">
                          {feature.description}
                        </p>
                      </div>
                      <div
                        className="flex flex-col mt-4 sm:mt-8 w-full overflow-hidden rounded-xl bg-slate-50 shadow sm:w-auto lg:mt-0 lg:w-[67.8125rem] h-auto sm:h-auto lg:h-[730px] relative"
                        onMouseOver={() => setIsTimerActive(false)}
                        onMouseLeave={() => setIsTimerActive(true)}
                      >
                        <div className="h-[28px] sm:h-[40px] bg-white flex items-center p-4 absolute left-0 right-0 top-0 z-50 border-b border-gray-300">
                          <div className="flex gap-x-2">
                            <span className="w-1.5 sm:w-2 h-1.5 sm:h-2 rounded-full bg-gray-300"></span>
                            <span className="w-1.5 sm:w-2 h-1.5 sm:h-2 rounded-full bg-gray-300"></span>
                            <span className="w-1.5 sm:w-2 h-1.5 sm:h-2 rounded-full bg-gray-300"></span>
                          </div>
                          <div className="w-full max-w-[180px] sm:max-w-[360px] bg-gray-100 h-4 sm:h-6 rounded border border-gray-200 flex items-center justify-center text-gray-400 mt-[1px] sm:mt-0 text-[10px] sm:text-xs font-medium text-center mx-auto">
                            elmbase.com
                          </div>
                        </div>
                        <div className={classNames("flex")}>
                          <Image
                            className={classNames(
                              "sm:object-cover object-center lg:object-left-top"
                            )}
                            src={feature.image}
                            alt={`feature-image-${ii + 1}`}
                            width={1085}
                            height={730}
                            priority
                            unoptimized
                          />
                        </div>
                      </div>
                    </div>
                  )
              )}
            </>
          }
        </Tab.Group>
        {footerText && (
          <span className="text-white text-xs block mt-10 lg:mt-16">
            {footerText}
          </span>
        )}
      </Container>
    </section>
  );
};

export default FeaturesSection;
